import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PublicNavbar from "@/components/layout/PublicNavbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, Check, Loader2 } from "lucide-react";

const SINGLE_IMG = "https://static.prod-images.emergentagent.com/jobs/6d089f85-473b-4449-a6e3-5eb88daae93b/images/4e87d0130c90ae98ba68f87645f227d576e341858a9ef3bbbbba7ff831b686db.jpeg";
const categoryLabels = { single: "Single Room", shared: "Shared Room", premium: "Premium Suite", accessible: "Accessible Room" };
const statusColors = { available: "bg-emerald-100 text-emerald-700", reserved: "bg-amber-100 text-amber-700", occupied: "bg-sky-100 text-sky-700", maintenance: "bg-slate-100 text-slate-500" };

export default function RoomDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/rooms/${id}`).then((r) => setRoom(r.data)).catch(() => navigate("/rooms")).finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-sky-500" /></div>;
  if (!room) return null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="room-detail-page">
      <PublicNavbar />
      <div className="pt-28 pb-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <button onClick={() => navigate("/rooms")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-sky-600 transition-colors mb-6" data-testid="back-to-rooms">
            <ArrowLeft className="h-4 w-4" /> Back to Rooms
          </button>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            <div className="lg:col-span-3 rounded-xl overflow-hidden border border-slate-200 shadow-sm">
              <img src={room.images?.[0] || SINGLE_IMG} alt={`Room ${room.number}`} className="w-full h-[420px] object-cover" data-testid="room-image" />
            </div>
            <div className="lg:col-span-2 space-y-5">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <Badge className={statusColors[room.status]}>{room.status}</Badge>
                  <span className="text-xs font-bold text-sky-600 uppercase tracking-wider">{categoryLabels[room.category] || room.category}</span>
                </div>
                <h1 className="font-heading text-3xl font-black text-slate-900 tracking-tight" data-testid="room-title">Room {room.number}</h1>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="font-mono text-3xl font-bold text-slate-900">${room.price}</span>
                  <span className="text-slate-400 text-sm">/month</span>
                </div>
                <p className="text-sm text-slate-500">Deposit: ${room.deposit}</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-3">
                {[
                  ["Floor", room.floor],
                  ["Capacity", `${room.capacity} ${room.capacity > 1 ? "persons" : "person"}`],
                  ["Category", categoryLabels[room.category] || room.category],
                ].map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">{k}</span><span className="font-medium text-slate-900">{v}</span>
                  </div>
                ))}
              </div>
              {room.status === "available" && (
                <Button className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-none text-base font-bold" onClick={() => user ? navigate(`/tenant/apply/${room._id}`) : navigate("/register")} data-testid="apply-for-room-btn">
                  Apply for This Room
                </Button>
              )}
            </div>
          </div>
          {room.amenities?.length > 0 && (
            <div className="mt-10">
              <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">Amenities</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {room.amenities.map((a) => (
                  <div key={a} className="flex items-center gap-2 p-3 bg-white rounded-lg border border-slate-200 text-sm">
                    <Check className="h-4 w-4 text-sky-500 shrink-0" /><span className="text-slate-700">{a}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {room.description && (
            <div className="mt-8">
              <h2 className="font-heading text-xl font-bold text-slate-900 mb-3">Description</h2>
              <p className="text-slate-500 leading-relaxed">{room.description}</p>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
